'use client';

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight, Check, ExternalLink, Gift, Globe, Loader2, Lock, ShoppingCart } from 'lucide-react';
import toast from 'react-hot-toast';

import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import {
    useFunnelTemplates,
    useUpdateFunnelWithRouting,
    type FunnelTemplate,
} from '@/lib/api/funnels';
import { useStoreNavigate } from '@/hooks/use-app-navigate';
import { trpc } from '@/lib/api/trpc';
import { getNodeEditorConfig } from '@/components/builder/node-editor-registry';

import { StudioAutoDeployFrame } from './StudioAutoDeployFrame';
import {
    useFunnelCreation,
    type OrchestrationState,
    type ProgressInfo,
} from './useFunnelCreation';
import {
    applyOfferSplitPostDeploy,
    buildPrepareFunnel,
    resolveStarterOverrides,
    resolveTemplateId,
} from './buildWizardActions';
import { StepTrafficSource } from './wizard-steps/StepTrafficSource';
import { StepCheckoutStyle } from './wizard-steps/StepCheckoutStyle';
import { StepOfferStrategy } from './wizard-steps/StepOfferStrategy';
import { StepPaymentMethod } from './wizard-steps/StepPaymentMethod';
import { StepVipClub } from './wizard-steps/StepVipClub';
import { StepPortal } from './wizard-steps/StepPortal';
import {
    EMPTY_ANSWERS,
    type QuickstartAnswers,
    type WizardStepIndex,
} from './wizard-steps/types';

// ============================================================================
// Types
// ============================================================================

interface FunnelQuickstartWizardProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    storeId: string;
    mountPoint?: string;
    onSuccess?: (funnel: { id: string; name: string }, templateId: string) => void;
}

const TOTAL_STEPS: WizardStepIndex = 6;

// ============================================================================
// Step validation
// ============================================================================

function isStepValid(step: WizardStepIndex, answers: QuickstartAnswers): boolean {
    switch (step) {
        case 1:
            return answers.trafficSource !== null;
        case 2:
            return answers.checkoutStyle !== null;
        case 3:
            if (answers.offerStrategy === null) return false;
            if (answers.offerStrategy === 'geo' && answers.geoCountries.length === 0) return false;
            return true;
        case 4:
            return answers.paymentMode !== null;
        case 5:
            return answers.wantsClub !== null;
        case 6:
            return answers.wantsPortal !== null;
        default:
            return false;
    }
}

// ============================================================================
// Flat-list template card (skip mode)
// ============================================================================

const TEMPLATE_LOGOS: Record<string, string> = {
    'shopify-default': '/integrations/shopify-icon.svg',
    'woocommerce-default': '/integrations/woo-icon.svg',
};

const TEMPLATE_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
    'default': ShoppingCart,
    'with-offer': Gift,
    'geo-conditional': Globe,
};

const REQUIREMENT_TO_INTEGRATION: Record<string, string> = {
    'shopify_connected': 'shopify',
    'woocommerce_connected': 'woocommerce',
};

interface SkipTemplateCardProps {
    template: FunnelTemplate;
    selected: boolean;
    onSelect: () => void;
    onConnectIntegration?: (integrationId: string) => void;
}

function SkipTemplateCard({ template, selected, onSelect, onConnectIntegration }: SkipTemplateCardProps) {
    const { t } = useTranslation('common');
    const logoUrl = TEMPLATE_LOGOS[template.id];
    const IconComponent = TEMPLATE_ICONS[template.id] || ShoppingCart;
    const isDisabled = !template.available;
    const integrationToConnect = isDisabled && template.requirements.length > 0
        ? REQUIREMENT_TO_INTEGRATION[template.requirements[0]]
        : null;

    return (
        <button
            type="button"
            onClick={() => {
                if (isDisabled) return;
                onSelect();
            }}
            // aria-disabled keeps inner spans (e.g. "Connect now" link)
            // clickable — the native `disabled` attribute would swallow them.
            aria-disabled={isDisabled || undefined}
            className={cn(
                'group relative flex w-full flex-col items-start gap-2 rounded-xl border-2 p-4 text-left transition-all',
                selected
                    ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                    : 'border-border hover:border-primary/50 hover:bg-muted/50',
                isDisabled && 'cursor-not-allowed opacity-60 hover:border-border hover:bg-transparent',
            )}
        >
            {selected && (
                <div className="bg-primary absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full">
                    <Check className="h-3 w-3 text-white" />
                </div>
            )}
            {isDisabled && (
                <div className="absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full bg-muted">
                    <Lock className="text-muted-foreground h-3 w-3" />
                </div>
            )}
            <div className="flex items-center gap-3">
                <div
                    className={cn(
                        'flex h-10 w-10 items-center justify-center rounded-lg',
                        selected ? 'bg-primary/10' : 'bg-muted',
                        isDisabled && 'bg-muted/50',
                    )}
                >
                    {logoUrl ? (
                        <img src={logoUrl} alt={template.name} className="h-6 w-6" />
                    ) : (
                        <IconComponent className={cn('h-5 w-5', selected ? 'text-primary' : 'text-muted-foreground')} />
                    )}
                </div>
                <div className="flex flex-col">
                    <span className="font-medium">{template.name}</span>
                    {template.isDefault && (
                        <Badge variant="secondary" className="mt-0.5 w-fit text-xs">
                            {t('funnelTemplate.recommended')}
                        </Badge>
                    )}
                </div>
            </div>
            <p className="text-muted-foreground text-sm leading-relaxed">{template.description}</p>
            {isDisabled && template.unavailableReason && (
                <div className="flex items-center gap-2">
                    <p className="text-destructive text-xs">{template.unavailableReason}</p>
                    {integrationToConnect && onConnectIntegration && (
                        <span
                            role="link"
                            tabIndex={0}
                            onClick={(e) => {
                                e.stopPropagation();
                                onConnectIntegration(integrationToConnect);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.stopPropagation();
                                    onConnectIntegration(integrationToConnect);
                                }
                            }}
                            className="text-primary hover:text-primary/80 inline-flex cursor-pointer items-center gap-1 text-xs font-medium underline-offset-2 hover:underline"
                        >
                            {t('funnelTemplate.connectNow')}
                            <ExternalLink className="h-3 w-3" />
                        </span>
                    )}
                </div>
            )}
            {template.tags.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                    {template.tags.map((tag) => (
                        <Badge key={tag} variant="outline" className="text-xs">
                            {tag}
                        </Badge>
                    ))}
                </div>
            )}
        </button>
    );
}

// ============================================================================
// Progress dots
// ============================================================================

function ProgressDots({ step, total }: { step: WizardStepIndex; total: WizardStepIndex }) {
    return (
        <div className="flex items-center gap-1.5" aria-label={`Step ${step} of ${total}`}>
            {Array.from({ length: total }).map((_, i) => (
                <span
                    key={i}
                    className={cn(
                        'h-2 w-2 rounded-full transition-colors',
                        i < step ? 'bg-primary' : 'bg-muted',
                    )}
                />
            ))}
        </div>
    );
}

// ============================================================================
// Main wizard component
// ============================================================================

export function FunnelQuickstartWizard({
    open,
    onOpenChange,
    storeId,
    mountPoint = 'default',
    onSuccess,
}: FunnelQuickstartWizardProps) {
    const { t } = useTranslation('common');
    const { storeRoutes, navigate } = useStoreNavigate();

    const [step, setStep] = React.useState<WizardStepIndex>(1);
    const [answers, setAnswers] = React.useState<QuickstartAnswers>(EMPTY_ANSWERS);
    const [skipMode, setSkipMode] = React.useState(false);
    const [skipSelectedTemplateId, setSkipSelectedTemplateId] = React.useState<string | null>(null);
    // Track the post-deploy variant split so the dialog stays open while the
    // split is being persisted. Without this, the user sees the dialog close
    // briefly and the funnel save races against the next view.
    const [isPostProcessing, setIsPostProcessing] = React.useState(false);

    const { data: storeData } = trpc.stores.getById.useQuery({ storeId });
    const storeBranding = React.useMemo(() => {
        const cfg = (storeData?.config ?? null) as { primaryColor?: string; logoUrl?: string } | null;
        const storeName = storeData?.name ?? undefined;
        if (!cfg && !storeName) return undefined;
        return {
            primaryColor: cfg?.primaryColor,
            logoUrl: cfg?.logoUrl,
            storeName,
        };
    }, [storeData]);

    const { data: templates, isLoading: loadingTemplates } = useFunnelTemplates(storeId);
    const updateFunnelMutation = useUpdateFunnelWithRouting();

    const builderUrl = React.useMemo(
        () => getNodeEditorConfig('checkout' as any, { hasStudioSource: true }).iframeUrl
            || 'http://localhost:3003',
        [],
    );

    // Derive the dynamic hook params from the current answers. We rebuild these
    // each render (cheap pure functions) and the hook stores them in its
    // useCallback deps so the relevant closure refreshes when the user
    // navigates back and changes an answer.
    const starterOverrides = React.useMemo(
        () => resolveStarterOverrides(answers),
        [answers],
    );

    const persistFunnel = React.useCallback(
        async (params: { funnelId: string; storeId: string; config: any }) => {
            await updateFunnelMutation.mutateAsync({
                funnelId: params.funnelId,
                storeId: params.storeId,
                config: params.config,
            });
        },
        [updateFunnelMutation],
    );

    const prepareFunnel = React.useMemo(
        () => buildPrepareFunnel(answers, { storeId, persistFunnel }),
        [answers, storeId, persistFunnel],
    );

    const {
        orchestration,
        progressInfo,
        isBusy,
        createFromTemplate,
        reset,
        autoDeployFramePropsList,
        currentThumbnail,
    } = useFunnelCreation({
        storeId,
        mountPoint,
        digitalProduct: storeData?.digitalProduct ?? undefined,
        fallbackErrorMessage: t('funnelTemplate.createFailed'),
        starterOverrides,
        prepareFunnel,
    });

    // Reset everything when the dialog closes
    React.useEffect(() => {
        if (!open) {
            setStep(1);
            setAnswers(EMPTY_ANSWERS);
            setSkipMode(false);
            setSkipSelectedTemplateId(null);
            setIsPostProcessing(false);
            reset();
        }
    }, [open, reset]);

    // Auto-select default template the first time we enter skip mode and the
    // templates are available.
    React.useEffect(() => {
        if (!skipMode) return;
        if (!templates || templates.length === 0) return;
        if (skipSelectedTemplateId) return;
        const defaultTemplate = templates.find((t: FunnelTemplate) => t.isDefault && t.available);
        const firstAvailable = templates.find((t: FunnelTemplate) => t.available);
        setSkipSelectedTemplateId(defaultTemplate?.id || firstAvailable?.id || null);
    }, [skipMode, templates, skipSelectedTemplateId]);

    // Fire onSuccess + close once orchestration completes. For geo / A-B
    // strategies the upsell node needs an extra round-trip after auto-deploy:
    // we copy the deployed plugin config into N variants so the funnel
    // validator accepts the save. This has to happen after deploy because
    // the variants need a real pluginId to inherit.
    React.useEffect(() => {
        if (orchestration.phase !== 'done') return;

        const funnel = orchestration.funnel;
        const templateId = skipMode
            ? skipSelectedTemplateId
            : resolveTemplateId(answers);

        const finalize = () => {
            toast.success(t('funnelTemplate.createdSuccess'));
            onOpenChange(false);
            if (templateId) {
                onSuccess?.({ id: funnel.id, name: funnel.name }, templateId);
            }
        };

        const needsPostSplit = !skipMode
            && ((answers.offerStrategy === 'geo' && answers.geoCountries.length > 0)
                || answers.offerStrategy === 'split');

        if (!needsPostSplit) {
            finalize();
            return;
        }

        setIsPostProcessing(true);
        void (async () => {
            try {
                const splitConfig = applyOfferSplitPostDeploy(funnel.config, answers);
                if (splitConfig && splitConfig !== funnel.config) {
                    await updateFunnelMutation.mutateAsync({
                        funnelId: funnel.id,
                        storeId,
                        config: splitConfig as any,
                    });
                }
                setIsPostProcessing(false);
                finalize();
            } catch (err) {
                console.error('[FunnelQuickstartWizard] Post-deploy variant split failed:', err);
                toast.error(t('funnelTemplate.createFailed'));
                setIsPostProcessing(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orchestration.phase]);

    const handleConnectIntegration = (integrationId: string) => {
        onOpenChange(false);
        navigate(storeRoutes.INTEGRATION_CONFIG(integrationId));
    };

    const handleSetupClub = () => {
        onOpenChange(false);
        navigate(storeRoutes.CLUB_SETTINGS_TAB('membership'));
    };

    const onAnswersChange = (next: Partial<QuickstartAnswers>) => {
        setAnswers((prev) => ({ ...prev, ...next }));
    };

    const goBack = () => {
        if (step > 1) {
            setStep((s) => (s - 1) as WizardStepIndex);
        }
    };

    const goNext = () => {
        if (step < TOTAL_STEPS) {
            setStep((s) => (s + 1) as WizardStepIndex);
        }
    };

    const handleWizardCreate = () => {
        const templateId = resolveTemplateId(answers);
        if (!templateId) return;
        void createFromTemplate(templateId);
    };

    const handleSkipCreate = () => {
        if (!skipSelectedTemplateId) return;
        void createFromTemplate(skipSelectedTemplateId);
    };

    const isLastStep = step === TOTAL_STEPS;
    const currentStepValid = isStepValid(step, answers);
    const skipTemplate = templates?.find((t: FunnelTemplate) => t.id === skipSelectedTemplateId);
    const canSkipCreate = !!skipTemplate?.available && orchestration.phase === 'idle';

    // ========================================================================
    // Render
    // ========================================================================

    const renderWizardBody = () => {
        switch (step) {
            case 1:
                return (
                    <StepTrafficSource
                        answers={answers}
                        onChange={onAnswersChange}
                        storeId={storeId}
                        onConnectIntegration={handleConnectIntegration}
                    />
                );
            case 2:
                return <StepCheckoutStyle answers={answers} onChange={onAnswersChange} />;
            case 3:
                return <StepOfferStrategy answers={answers} onChange={onAnswersChange} />;
            case 4:
                return (
                    <StepPaymentMethod
                        answers={answers}
                        onChange={onAnswersChange}
                        storeId={storeId}
                        onConnectIntegration={handleConnectIntegration}
                    />
                );
            case 5:
                return (
                    <StepVipClub
                        answers={answers}
                        onChange={onAnswersChange}
                        storeId={storeId}
                        onSetupClub={handleSetupClub}
                    />
                );
            case 6:
                return <StepPortal answers={answers} onChange={onAnswersChange} />;
            default:
                return null;
        }
    };

    const renderSkipBody = () => {
        if (loadingTemplates) {
            return (
                <div className="flex items-center justify-center py-8">
                    <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
                </div>
            );
        }
        if (!templates || templates.length === 0) {
            return (
                <p className="text-muted-foreground py-8 text-center text-sm">
                    {t('funnelTemplate.noTemplates')}
                </p>
            );
        }
        return (
            <div className="flex flex-col gap-3">
                {templates.map((template) => (
                    <SkipTemplateCard
                        key={template.id}
                        template={template}
                        selected={skipSelectedTemplateId === template.id}
                        onSelect={() => template.available && setSkipSelectedTemplateId(template.id)}
                        onConnectIntegration={handleConnectIntegration}
                    />
                ))}
            </div>
        );
    };

    // The dialog stays in a "busy" UI while either the auto-deploy
    // orchestration is in flight or the post-deploy variant split is being
    // persisted. Keep the user from closing during both.
    const effectiveBusy = isBusy || isPostProcessing;

    return (
        <Dialog
            open={open}
            onOpenChange={(next) => {
                if (!next && effectiveBusy) return;
                onOpenChange(next);
            }}
        >
            <DialogContent className="sm:max-w-2xl">
                <DialogHeader>
                    <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                            <DialogTitle>
                                {effectiveBusy
                                    ? t('funnelTemplate.settingUpTitle')
                                    : skipMode
                                        ? t('funnelTemplate.dialogTitle')
                                        : t('funnelQuickstart.dialogTitle')}
                            </DialogTitle>
                            <DialogDescription>
                                {effectiveBusy
                                    ? t('funnelTemplate.settingUpDescription')
                                    : skipMode
                                        ? t('funnelTemplate.dialogDescription')
                                        : t('funnelQuickstart.dialogDescription')}
                            </DialogDescription>
                        </div>
                        {!effectiveBusy && !skipMode && (
                            <ProgressDots step={step} total={TOTAL_STEPS} />
                        )}
                    </div>
                </DialogHeader>

                <div className="py-4 px-1 -mx-1 overflow-y-auto max-h-[612px]">
                    {orchestration.phase === 'error' ? (
                        <div className="flex flex-col items-center justify-center gap-3 py-10 text-center">
                            <p className="text-destructive text-sm font-medium">{orchestration.message}</p>
                            <Button variant="outline" onClick={reset}>
                                {t('funnelTemplate.cancel')}
                            </Button>
                        </div>
                    ) : effectiveBusy ? (
                        <SetupProgress
                            progressInfo={progressInfo}
                            phase={isPostProcessing ? 'saving' : orchestration.phase}
                            currentThumbnail={currentThumbnail}
                        />
                    ) : skipMode ? (
                        renderSkipBody()
                    ) : (
                        renderWizardBody()
                    )}
                </div>

                {/* Footer */}
                {!effectiveBusy && orchestration.phase !== 'error' && (
                    <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                            {skipMode ? (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setSkipMode(false)}
                                >
                                    <ArrowLeft className="mr-1 h-4 w-4" />
                                    {t('funnelQuickstart.footer.backToQuiz')}
                                </Button>
                            ) : (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setSkipMode(true)}
                                >
                                    {t('funnelQuickstart.footer.skipQuiz')}
                                </Button>
                            )}
                        </div>

                        <div className="flex items-center gap-3">
                            {!skipMode && step > 1 && (
                                <Button variant="outline" onClick={goBack}>
                                    <ArrowLeft className="mr-1 h-4 w-4" />
                                    {t('funnelQuickstart.footer.back')}
                                </Button>
                            )}
                            <Button
                                variant="outline"
                                onClick={() => onOpenChange(false)}
                            >
                                {t('funnelTemplate.cancel')}
                            </Button>
                            {skipMode ? (
                                <Button onClick={handleSkipCreate} disabled={!canSkipCreate}>
                                    {t('funnelTemplate.createButton')}
                                </Button>
                            ) : isLastStep ? (
                                <Button onClick={handleWizardCreate} disabled={!currentStepValid}>
                                    {t('funnelQuickstart.footer.createFunnel')}
                                </Button>
                            ) : (
                                <Button onClick={goNext} disabled={!currentStepValid}>
                                    {t('funnelQuickstart.footer.next')}
                                    <ArrowRight className="ml-1 h-4 w-4" />
                                </Button>
                            )}
                        </div>
                    </div>
                )}

                {autoDeployFramePropsList.map((frame) => (
                    <StudioAutoDeployFrame
                        key={frame.key}
                        builderUrl={builderUrl}
                        context={frame.context}
                        starter={frame.starter}
                        storeBranding={storeBranding}
                        onComplete={frame.onComplete}
                        onError={frame.onError}
                    />
                ))}
            </DialogContent>
        </Dialog>
    );
}

// ============================================================================
// SetupProgress (duplicated from FunnelTemplateDialog — TODO: extract in Phase C)
// ============================================================================

interface SetupProgressProps {
    progressInfo: ProgressInfo | null;
    phase: OrchestrationState['phase'];
    currentThumbnail?: string;
}

function SetupProgress({ progressInfo, phase, currentThumbnail }: SetupProgressProps) {
    const { t } = useTranslation('common');
    const [rampOffset, setRampOffset] = React.useState(0);
    // Cache the most recently seen thumbnail so it stays on screen through
    // the brief `saving` / phase-transition gaps between auto-deploy steps —
    // otherwise the image flashes away every time a deploy completes.
    const lastThumbnailRef = React.useRef<string | undefined>(undefined);
    if (currentThumbnail && currentThumbnail !== lastThumbnailRef.current) {
        lastThumbnailRef.current = currentThumbnail;
    }
    const displayThumbnail = lastThumbnailRef.current;

    React.useEffect(() => {
        setRampOffset(0);
        if (phase === 'creating' || phase === 'preparing') {
            const interval = window.setInterval(() => {
                setRampOffset((prev) => Math.min(0.8, prev + 0.04));
            }, 200);
            return () => window.clearInterval(interval);
        }
        return undefined;
    }, [phase]);

    const total = progressInfo?.total ?? 0;
    const done = progressInfo?.done ?? 0;
    const currentName = progressInfo?.currentName ?? null;

    let displayValue: number;
    let statusText: string;
    switch (phase) {
        case 'creating':
            displayValue = Math.round(25 * rampOffset);
            statusText = t('funnelTemplate.statusCreating');
            break;
        case 'preparing':
            displayValue = 25 + Math.round(10 * rampOffset);
            statusText = t('funnelTemplate.statusPreparing');
            break;
        case 'deploying-parallel': {
            // `done` is the count of steps that have finished deploying; fill
            // the 35–90% band proportionally as the concurrent batch drains.
            const fill = total > 0 ? done / total : 0;
            displayValue = 35 + Math.round(55 * Math.min(0.99, fill));
            statusText = t('funnelTemplate.statusDeployingNoName', {
                done: Math.min(done + 1, Math.max(1, total)),
                total: Math.max(1, total),
            });
            break;
        }
        case 'saving':
            // Single final persist after every step deployed — own 90–100% slice.
            displayValue = 90 + Math.round(10 * (0.5 + rampOffset * 0.5));
            statusText = t('funnelTemplate.statusSaving');
            break;
        case 'done':
            displayValue = 100;
            statusText = t('funnelTemplate.statusDone');
            break;
        default:
            displayValue = 0;
            statusText = t('funnelTemplate.settingUpTitle');
    }

    displayValue = Math.min(100, displayValue);

    return (
        <div className="flex flex-col items-center justify-center gap-4 py-8">
            {displayThumbnail ? (
                <div className="bg-muted/40 relative flex h-40 w-full max-w-md items-center justify-center overflow-hidden rounded-lg border">
                    <img
                        src={displayThumbnail}
                        alt={currentName ?? ''}
                        className="h-full w-full object-contain opacity-30"
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                        <Loader2 className="text-primary h-10 w-10 animate-spin" />
                    </div>
                </div>
            ) : (
                <Loader2 className="text-primary h-8 w-8 animate-spin" />
            )}
            <Progress value={displayValue} className="w-full max-w-md" />
            <p className="text-foreground text-sm font-medium" aria-live="polite">
                {statusText}
            </p>
            <p className="text-muted-foreground text-xs">{displayValue}%</p>
        </div>
    );
}
